import React, { useMemo } from "react";
import { upperFirst } from "lodash";

import HeaderImage from "assets/images/nzCaseStudy/header.png";

import "../styles.css";

const rowsToAdd = [
    { id: "energyConsumption", label: "Energy Consumption" },
    { id: "comfort", label: "Comfort" },
    { id: "buildingCondition", label: "Building Condition" },
    { id: "generation", label: "Renewables" },
    { id: "wall", label: "Wall Insulation" },
    { id: "ceiling", label: "Ceiling Insulation" },
    { id: "basement", label: "Foundation Insulation" },
    { id: "window", label: "Windows" },
    { id: "ventilation", label: "Ventilation" },
    { id: "spaceHeating", label: "Heating" },
    { id: "spaceCooling", label: "Cooling" },
    { id: "heatLossAndGain", label: "Heat Loss & Gain" },
    { id: "domesticHotWater", label: "Hot Water" },
    { id: "electricalPanelSize", label: "Electrical Panel Size" },
];

const NZReportPDFPage2 = ({
    beforeComfort,
    afterComfort,
    beforeBuildingCondition,
    afterBuildingCondition,
    beforeElectricalPanelSize,
    afterElectricalPanelSize,
    nzPage2Content = {},
}) => {
    const {
        nzBeforeEnergy = "",
        nzAfterEnergy = "",
        nzBeforeHeatLossGain = "",
        nzAfterHeatLossGain = "",
        nzBeforeGeneration = "",
        nzAfterGeneration = "",
        nzBeforeWall = "",
        nzAfterWall = "",
        nzBeforeCeiling = "",
        nzAfterCeiling = "",
        nzBeforeFoundation = "",
        nzAfterFoundation = "",
        nzBeforeWindow = "",
        nzAfterWindow = "",
        nzBeforeVentilation = "",
        nzAfterVentilation = "",
        nzBeforeHeating = "",
        nzAfterHeating = "",
        nzBeforeCooling = "",
        nzAfterCooling = "",
        nzBeforeDHW = "",
        nzAfterDHW = "",
    } = nzPage2Content;

    const tableRows = useMemo(() => {
        const rows = [["", "BEFORE", "AFTER"]];

        //Only show electrical panel size if both before and after electrical panel size are provided
        const filteredRowsToAdd = rowsToAdd.filter(
            ({ id: rowId }) =>
                rowId !== "electricalPanelSize" ||
                (rowId === "electricalPanelSize" && beforeElectricalPanelSize && afterElectricalPanelSize)
        );

        filteredRowsToAdd.forEach(({ id: rowId, label: rowLabel }) => {
            if (rowId === "energyConsumption") {
                rows.push([rowLabel, nzBeforeEnergy, nzAfterEnergy]);
            }

            if (rowId === "heatLossAndGain") {
                rows.push([rowLabel, nzBeforeHeatLossGain, nzAfterHeatLossGain]);
            }

            // User Input
            if (rowId === "comfort") {
                rows.push([rowLabel, beforeComfort, afterComfort]);
            }

            // User Input
            if (rowId === "buildingCondition") {
                rows.push([rowLabel, beforeBuildingCondition, afterBuildingCondition]);
            }

            // User Input
            if (rowId === "electricalPanelSize") {
                rows.push([rowLabel, `${beforeElectricalPanelSize} A`, `${afterElectricalPanelSize} A`]);
            }

            if (rowId === "generation") {
               rows.push([rowLabel, nzBeforeGeneration, nzAfterGeneration]);
            }

            if (rowId === "wall") {
                rows.push([rowLabel, nzBeforeWall, nzAfterWall]);
            }

            if (rowId === "ceiling") {
                rows.push([rowLabel, nzBeforeCeiling, nzAfterCeiling]);
            }

            if (rowId === "basement") {
                rows.push([rowLabel, nzBeforeFoundation, nzAfterFoundation]);
            }

            if (rowId === "window") {
                rows.push([rowLabel, nzBeforeWindow, nzAfterWindow]);
            }

            if (rowId === "ventilation") {
                rows.push([rowLabel, nzBeforeVentilation, nzAfterVentilation]);
            }

            if (rowId === "spaceHeating") {
                rows.push([rowLabel, nzBeforeHeating, nzAfterHeating]);
            }

            if (rowId === "spaceCooling") {
                rows.push([rowLabel, nzBeforeCooling, nzAfterCooling]);
            }

            if (rowId === "domesticHotWater") {
               rows.push([rowLabel, nzBeforeDHW, nzAfterDHW]);
            }
        });

        // Add an empty row so there's space at the end of the page
        rows.push(["", "", ""]);

        return rows;
    }, [
        afterBuildingCondition,
        afterComfort,
        beforeBuildingCondition,
        beforeComfort,
        afterElectricalPanelSize,
        beforeElectricalPanelSize,
        nzPage2Content,
    ]);

    return (
        <div className="bodyNZPDF">
            <img src={HeaderImage} alt="header" />
            <span className="headerTextNZPDF">
                NET ZERO PLAN
            </span>
            <div className="pageContainerNZPDF" style={{ padding: "0 0 0 40px" }}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        flex: 1,
                    }}
                >
                    {tableRows.map((row, index) => (
                        <div
                            key={index}
                            style={{
                                display: "flex",
                                width: "100%",
                                flex: 1,
                            }}
                        >
                            {row.map((cell, cellIndex) => (
                                <div
                                    key={cell + cellIndex}
                                    style={{
                                        background: cellIndex === 2 && "#E6E7E9",
                                        flex: cellIndex === 0 ? "0 0 18%" : 1,
                                        padding: index === 0 ? "15px 0" : "5px 0",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            background:
                                                index === 0
                                                    ? cellIndex === 1
                                                        ? "#626261"
                                                        : cellIndex === 2 && "#93bb49"
                                                    : "",
                                            fontSize: index === 0 ? "26px" : cellIndex === 0 ? "18px" : "16px",
                                            fontWeight: index === 0 ? "800" : cellIndex === 0 ? "bold" : "normal",
                                            color:
                                                index === 0
                                                    ? cellIndex === 1
                                                        ? "#fff"
                                                        : "#626261"
                                                    : cellIndex === 0
                                                    ? "#626261"
                                                    : "#231f20",
                                            padding:
                                                index !== 0
                                                    ? (cellIndex === 1 || cellIndex === 2) && " 0 30px"
                                                    : "15px 0",
                                            margin:
                                                index === 0 ? (cellIndex === 1 || cellIndex === 2) && "0 15px" : "0",
                                            wordWrap: "break-word",
                                            display: index === 0 && "flex",
                                            justifyContent: index === 0 ? "center" : "flex-start",
                                            alignItems: index === 0 ? "center" : "flex-start",
                                            width: "100%",
                                        }}
                                    >
                                        {cellIndex === 0 ? cell.toUpperCase() : upperFirst(cell)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default NZReportPDFPage2;
